import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import TopicIllustration, { TopicVariant } from '../components/TopicIllustration';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { getCompletedTopicsForDate } from '../utils/quizCompletionCookie';

const TOPICS: Array<{ slot: number; name: string; variant: TopicVariant }> = [
  { slot: 1, name: 'Pop Culture', variant: 'pop-culture' },
  { slot: 2, name: 'Science & Nature', variant: 'science-nature' },
  { slot: 3, name: 'History & Politics', variant: 'history-politics' },
  { slot: 4, name: 'Geography & World', variant: 'geography-world' },
  { slot: 5, name: 'Sports & Games', variant: 'sports-games' },
];

const Compete: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [topicStatus, setTopicStatus] = useState<Record<number, { played: boolean; score?: number; questionsCorrect?: number }>>({});
  const [topicAvailability, setTopicAvailability] = useState<Record<number, boolean>>({});
  const [resetCountdown, setResetCountdown] = useState('--:--:--');
  const [currentStreak, setCurrentStreak] = useState<number | null>(null);
  const isPremium = user?.isPremium ?? false;

  useDocumentTitle('Compete | Cognidaily');

  useEffect(() => {
    const updateResetCountdown = () => {
      const now = Date.now();
      const offsetMs = 10 * 60 * 60 * 1000;
      const msPerDay = 24 * 60 * 60 * 1000;
      const nowInTargetZone = now + offsetMs;
      const startOfDay = Math.floor(nowInTargetZone / msPerDay) * msPerDay;
      const nextMidnight = startOfDay + msPerDay;
      const timeUntilReset = Math.max(0, nextMidnight - nowInTargetZone);

      const totalSeconds = Math.floor(timeUntilReset / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      setResetCountdown(
        [hours, minutes, seconds]
          .map((u) => u.toString().padStart(2, '0'))
          .join(':')
      );
    };

    updateResetCountdown();
    const interval = window.setInterval(updateResetCountdown, 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const response = await axios.get('/api/quiz/availability');
        if (response.data.success && response.data.data?.availability) {
          setTopicAvailability(response.data.data.availability);
        } else {
          setTopicAvailability({});
        }
      } catch {
        setTopicAvailability({});
      }
    };
    fetchAvailability();
  }, [location.pathname]);

  useEffect(() => {
    const checkPlayStatus = async () => {
      if (!isAuthenticated) {
        setTopicStatus({});
        return;
      }
      try {
        const response = await axios.get('/api/quiz/today');
        if (response.data.success && response.data.data?.topics) {
          setTopicStatus(response.data.data.topics);
        } else {
          setTopicStatus({});
        }
      } catch {
        setTopicStatus({});
      }
    };
    checkPlayStatus();
  }, [isAuthenticated, location.pathname]);

  // Refetch when tab becomes visible (e.g. user completed quiz in another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        axios.get('/api/quiz/availability')
          .then((response) => {
            if (response.data.success && response.data.data?.availability) {
              setTopicAvailability(response.data.data.availability);
            }
          })
          .catch(() => {});
        if (isAuthenticated) {
          axios.get('/api/quiz/today')
            .then((response) => {
              if (response.data.success && response.data.data?.topics) {
                setTopicStatus(response.data.data.topics);
              }
            })
            .catch(() => {});
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchStreak = async () => {
      if (!isAuthenticated) {
        setCurrentStreak(null);
        return;
      }
      try {
        const response = await axios.get('/api/user/statistics', {
          params: { quizType: 'main' },
        });
        if (response.data.success && response.data.data?.overall) {
          setCurrentStreak(response.data.data.overall.currentStreak || 0);
        }
      } catch {
        setCurrentStreak(null);
      }
    };
    fetchStreak();
  }, [isAuthenticated]);

  const handleTopicClick = (slot: number) => {
    if (topicAvailability[slot] === false) return;
    navigate(`/quiz?topic=${slot}`);
  };

  const guestCompleted = getCompletedTopicsForDate();
  const getTopicPlayed = (slot: number) => {
    if (isAuthenticated && topicStatus[slot]?.played) return true;
    if (!isAuthenticated && guestCompleted.includes(slot)) return true;
    return false;
  };

  const allPlayed = TOPICS.every((t) => getTopicPlayed(t.slot));

  return (
    <>
      <SEOHead
        title="Compete | Cognidaily"
        description="5 daily quizzes. 10 questions each. One attempt per topic per day."
      />
      <article className="compete-page">
        <section className="compete-page__header">
          <div className="compete-page__header-text">
            <h1>Daily Quizzes</h1>
            <p>5 topics, 10 questions each. One attempt per topic per day.</p>
            {isAuthenticated && currentStreak !== null && currentStreak > 0 && (
              <p>{currentStreak}-day streak</p>
            )}
          </div>
          <p className="countdown-badge">
            <span className="countdown-badge__label">Today&apos;s time remaining:</span>{' '}
            <span className="countdown-badge__value">{resetCountdown}</span>
          </p>
        </section>

        <div className="compete-topic-grid">
          {TOPICS.map(({ slot, name, variant }) => {
            const played = getTopicPlayed(slot);
            const isAvailable = topicAvailability[slot] !== false;
            return (
              <div
                key={slot}
                className={`compete-topic-card${!isAvailable ? ' compete-topic-card--unavailable' : ''}`}
              >
                <div className="compete-topic-card__image">
                  <TopicIllustration variant={variant} idSuffix={`topic-${slot}`} />
                </div>
                <div className="compete-topic-card__content">
                  <h3>{name}</h3>
                  {played ? (
                    <span className="compete-topic-card__check" aria-hidden="true">✓</span>
                  ) : !isAvailable ? (
                    <span className="compete-topic-card__unavailable" aria-live="polite">
                      Unavailable
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary btn-compact"
                      onClick={() => handleTopicClick(slot)}
                    >
                      Play
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {allPlayed && <p>All 5 completed today</p>}

        <div className="compete-action-cards">
          <div className="compete-action-card">
            <div className="compete-action-card__image">
              <TopicIllustration variant="leaderboard" idSuffix="compete-leaderboard" />
            </div>
            <div className="compete-action-card__content">
              <div className="compete-action-card__text">
                <h2>Leaderboard</h2>
                <p>Daily, weekly, monthly rankings.</p>
              </div>
              <Link to="/leaderboard" className="btn btn-primary btn-compact">
                Leaderboard
              </Link>
            </div>
          </div>
          {!isPremium && (
            <div className="compete-action-card">
              <div className="compete-action-card__image">
                <TopicIllustration variant="premium" idSuffix="compete-premium" />
              </div>
              <div className="compete-action-card__content">
                <div className="compete-action-card__text">
                  <h2>Premium</h2>
                  <p>Quiz history and more.</p>
                </div>
                <Link to="/premium" className="btn btn-primary btn-compact">
                  Premium
                </Link>
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
};

export default Compete;
